/**
 * Secilen element icin CSS secici adaylari uretir.
 *
 * Adaylar dayanikliliga gore siralanir: id ve data-* attribute'lari tema/derleme
 * degisikliklerinden etkilenmez; hash benzeri class'lar ve nth-child yollari kirilgandir.
 */

const STABILITY = { STABLE: "stable", MEDIUM: "medium", FRAGILE: "fragile" };

// Urun/fiyat gibi anlamli veri tasiyan attribute'lar oncelikli aday olur
const DATA_ATTR_REGEX = /^data-/;
const IGNORED_ATTRS = ["data-supsis-view", "data-reactid", "data-v-"];

// Derleyicilerin urettigi class'lar (css-1a2b3c, jsx-123456, sc-AbCdEf)
const HASH_CLASS_REGEX = /(^|[-_])[a-z]*\d{4,}|^(css|jsx|sc)-[a-z0-9]{5,}$/i;

function safeCount(selector) {
    try {
        return document.querySelectorAll(selector).length;
    } catch (e) {
        return -1;
    }
}

function cssEscape(value) {
    // Sayi ile baslayan veya ozel karakter tasiyan degerler icin kacis
    return String(value).replace(/([^\w-])/g, "\\$1");
}

function classList(element) {
    const raw = typeof element.className === "string" ? element.className : "";
    return raw
        .trim()
        .split(/\s+/)
        .filter(function (name) {
            return !!name && name.length < 40;
        });
}

function isStableClass(name) {
    return !HASH_CLASS_REGEX.test(name);
}

function dataAttributes(element) {
    const result = [];
    if (!element.attributes) return result;
    for (let i = 0; i < element.attributes.length; i++) {
        const attr = element.attributes[i];
        if (!DATA_ATTR_REGEX.test(attr.name)) continue;
        let ignored = false;
        for (let j = 0; j < IGNORED_ATTRS.length; j++) {
            if (attr.name.indexOf(IGNORED_ATTRS[j]) === 0) ignored = true;
        }
        if (ignored) continue;
        result.push({ name: attr.name, value: String(attr.value || "").slice(0, 200) });
    }
    return result;
}

/** Element icin nth-child tabanli tam yol (son care). */
function nthChildPath(element) {
    const parts = [];
    let node = element;
    let depth = 0;

    while (node && node.nodeType === 1 && depth < 6) {
        const tag = node.tagName.toLowerCase();
        if (tag === "html" || tag === "body") break;
        if (node.id) {
            parts.unshift("#" + cssEscape(node.id));
            break;
        }
        const parent = node.parentElement;
        if (!parent) {
            parts.unshift(tag);
            break;
        }
        let index = 1;
        let sibling = node.previousElementSibling;
        while (sibling) {
            index++;
            sibling = sibling.previousElementSibling;
        }
        parts.unshift(tag + ":nth-child(" + index + ")");
        node = parent;
        depth++;
    }

    return parts.join(" > ");
}

function push(candidates, selector, stability) {
    if (!selector) return;
    for (let i = 0; i < candidates.length; i++) {
        if (candidates[i].selector === selector) return;
    }
    const count = safeCount(selector);
    if (count <= 0) return;
    candidates.push({ selector: selector, count: count, stability: stability });
}

/**
 * @param {Element} element
 * @returns {Array} [{ selector, count, stability }] dayanikliliga gore sirali
 */
export function buildCandidates(element) {
    if (!element || element.nodeType !== 1) return [];

    const candidates = [];
    const tag = element.tagName.toLowerCase();
    const classes = classList(element);
    const stableClasses = classes.filter(isStableClass);

    if (element.id) push(candidates, "#" + cssEscape(element.id), STABILITY.STABLE);

    const attrs = dataAttributes(element);
    for (let i = 0; i < attrs.length; i++) {
        // Once degersiz (varlik) secici: data-sku degeri her urunde degisir, attribute sabittir
        push(candidates, "[" + attrs[i].name + "]", STABILITY.STABLE);
    }

    if (stableClasses.length > 0) {
        push(candidates, "." + stableClasses.map(cssEscape).join("."), STABILITY.STABLE);
        push(candidates, tag + "." + cssEscape(stableClasses[0]), STABILITY.STABLE);
        if (stableClasses.length > 1) {
            push(candidates, "." + cssEscape(stableClasses[0]), STABILITY.MEDIUM);
        }
    }

    // Ust elementle daraltilmis secici: benzer kartlarda ayirt edici olur
    const parent = element.parentElement;
    if (parent && parent.tagName) {
        const parentClasses = classList(parent).filter(isStableClass);
        if (parentClasses.length > 0) {
            const prefix = "." + cssEscape(parentClasses[0]);
            const suffix = stableClasses.length > 0 ? tag + "." + cssEscape(stableClasses[0]) : tag;
            push(candidates, prefix + " " + suffix, STABILITY.MEDIUM);
        }
    }

    const unstableClasses = classes.filter(function (name) {
        return !isStableClass(name);
    });
    if (candidates.length === 0 && unstableClasses.length > 0) {
        push(candidates, "." + cssEscape(unstableClasses[0]), STABILITY.FRAGILE);
    }

    push(candidates, nthChildPath(element), STABILITY.FRAGILE);

    const order = { stable: 0, medium: 1, fragile: 2 };
    return candidates.sort(function (a, b) {
        return order[a.stability] - order[b.stability];
    });
}

/**
 * Panelin "property'ye cevrilecek alanlar" listesi icin element ozeti.
 * Form alanlarinin degerleri toplanmaz; yalnizca secici onerilir.
 */
export function describeSelection(element) {
    const attrs = dataAttributes(element);
    const anchor = element.closest ? element.closest("a[href]") : null;
    const text = String(element.innerText || element.textContent || "")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 200);

    return {
        candidates: buildCandidates(element),
        tag: element.tagName.toLowerCase(),
        id: element.id ? String(element.id).slice(0, 120) : "",
        classes: classList(element),
        attrs: attrs,
        text: text,
        href: anchor ? String(anchor.getAttribute("href") || "").slice(0, 300) : "",
        html: String(element.outerHTML || "").slice(0, 500),
        pathname: window.location.pathname,
    };
}

export { STABILITY };



//////////////////
// WEBPACK FOOTER
// ./static/src/tracker/editor/selectorCandidates.js
// module id = null
// module chunks = 